import {
  fetchExtensionGroups,
  fetchExtensions,
  fetchInboundRoutes,
  fetchIvrDetail,
  fetchIVRs,
  fetchOutboundRouteList,
  fetchPbxSystemTime,
  fetchPhones,
  fetchRecentInboundFirstLegs,
  fetchQueueDetail,
  fetchQueues,
  fetchTrunks,
  getInboundRoute,
  getOutboundRoute,
} from '@/services/api';
import type {
  CallRecord,
  Extension,
  ExtensionGroupInfo,
  InboundRoute,
  IvrDetail,
  OutboundDialPattern,
  OutboundExtListItem,
  OutboundRoute,
  PhoneDevice,
  QueueDetail,
  RouteDestinationInfo,
  TimeConditionPeriod,
  TrunkInfo,
} from '@/types';
import {
  activePeriodIndex,
  currentDestinationKey,
  formatDaysOfWeek,
  formatPeriod,
  parsePbxClock,
  type PbxClock,
} from '@/lib/reports/timePeriods';

export type ExtensionDirectoryRow = {
  number: string;
  name: string;
  email: string;
  phone: string;
  groupKey: string;
};

export type ExtensionDirectoryGroup = {
  key: string;
  label: string;
  badge: string;
  color: string;
  phoneHint?: string;
  rows: ExtensionDirectoryRow[];
};

export type ExtensionDirectoryReport = {
  customerName: string;
  generatedAt: string;
  total: number;
  groups: ExtensionDirectoryGroup[];
  ungroupedCount: number;
};

export type RouteDestSummary = {
  type: string;
  value: string;
  label: string;
  kind: 'queue' | 'extension' | 'ivr' | 'ring_group' | 'other';
};

export type ResolvedIvr = IvrDetail & {
  menuResolved: Array<
    IvrDetail['menu'][number] & {
      resolvedLabel: string;
      queueDetail?: QueueDetail;
    }
  >;
  timeoutLabel?: string;
  invalidLabel?: string;
};

export type DestinationBranch = {
  title: string;
  dest: RouteDestSummary;
  ivr?: ResolvedIvr;
  queue?: QueueDetail;
  /** Where calls on this route go right now. */
  live?: boolean;
  /** The PBX did not say where this branch goes. */
  unknown?: boolean;
  /** Custom time periods: the "any other time" or holidays branch. */
  outsidePeriods?: boolean;
  /** Worked out from recent calls, because the PBX does not report it. */
  fromHistory?: boolean;
  note?: string;
};

export type InboundRouteReportCard = {
  id: number;
  name: string;
  trunksLabel: string;
  didsLabel: string;
  timeConditionMode: string;
  scheduleLabel: string;
  timezoneLabel?: string;
  /** When time conditions are off, single always-on destination. */
  alwaysDest?: DestinationBranch;
  officeHours?: DestinationBranch;
  afterHours?: DestinationBranch;
  holidays?: DestinationBranch;
  /** time_condition custom: one branch per time period, then "any other time". */
  periodLanes?: DestinationBranch[];
  /** Queues related to this route's branch but not used on the office IVR menu. */
  otherQueues: QueueDetail[];
  routeMode?: string;
};

export type InboundRoutingReport = {
  customerName: string;
  generatedAt: string;
  cards: InboundRouteReportCard[];
  /** IVRs no branch reaches, listed when some branch's destination is unknown. */
  unlinkedIvrs: ResolvedIvr[];
  queues: QueueDetail[];
  ivrs: IvrDetail[];
};

export type OutboundDialPatternSummary = {
  pattern: string;
  strip?: number;
  prepend?: string;
  label: string;
};

export type OutboundExtensionMember = {
  number: string;
  name: string;
};

export type OutboundPermissionEntry = {
  /** extension | ext_group | organization | other */
  kind: 'extension' | 'ext_group' | 'organization' | 'other';
  id?: string;
  label: string;
  /** For groups/orgs: number of resolved members */
  memberCount: number;
  members: OutboundExtensionMember[];
  /** True when group was configured but membership could not be resolved */
  membersUnresolved?: boolean;
};

export type OutboundRouteReportCard = {
  id: number;
  name: string;
  pos?: number;
  trunksLabel: string;
  outboundCidLabel: string;
  pinLabel: string;
  dialPatterns: OutboundDialPatternSummary[];
  permissions: OutboundPermissionEntry[];
  /** Flattened unique extensions that can dial out via this route */
  allDialers: OutboundExtensionMember[];
  dialerCount: number;
};

export type OutboundRoutingReport = {
  customerName: string;
  generatedAt: string;
  cards: OutboundRouteReportCard[];
  totalRoutes: number;
  totalUniqueDialers: number;
};

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function extensionDisplayName(ext: Extension): string {
  return (
    ext.display_name ||
    [ext.first_name, ext.last_name].filter(Boolean).join(' ').trim() ||
    ext.username ||
    ext.caller_id_name ||
    '—'
  );
}

function isOnlineEndpoint(status: unknown): boolean {
  if (!status || typeof status !== 'object') return false;
  const s = status as { status?: number; status_list?: Array<{ status?: number }> };
  if (s.status === 1) return true;
  // Yeastar puts live SIP registrations in status_list even when top-level status is 0
  return Array.isArray(s.status_list) && s.status_list.some((row) => row?.status === 1);
}

function handsetLabel(device: PhoneDevice): string {
  const vendor = (device.vendor || '').trim();
  const model = (device.model || '').trim();
  if (vendor && model) {
    // Avoid "Yealink Yealink T46S" style doubles
    if (model.toLowerCase().startsWith(vendor.toLowerCase())) return model;
    return `${vendor} ${model}`;
  }
  return model || (device.template_name || '').trim() || 'Desk phone';
}

/**
 * Phone column combines:
 * 1. Mobile number configured on the extension (when set)
 * 2. Autoprovisioned handset make+model (from phone/search)
 * 3. Linkus App when softphone-only / no provisioned handset
 */
function phoneLabelForExt(ext: Extension, phones: PhoneDevice[]): string {
  const mobile = (ext.mobile_number || '').trim();

  const devices = phones.filter((p) => p.assigned_ext_num === ext.number);
  const models = [...new Set(devices.map(handsetLabel).filter(Boolean))];

  const online = ext.online_status || {};
  const linkusOnline =
    isOnlineEndpoint(online.linkus_mobile) ||
    isOnlineEndpoint(online.linkus_desktop) ||
    isOnlineEndpoint(online.linkus_web);
  const sipOnline = isOnlineEndpoint(online.sip_phone);

  const endpointParts: string[] = [];
  if (models.length) {
    endpointParts.push(...models);
    if (linkusOnline) endpointParts.push('Linkus');
  } else if (sipOnline && !linkusOnline) {
    // Registered SIP device not present in Auto Provisioning inventory
    endpointParts.push('SIP phone');
  } else {
    endpointParts.push('Linkus App');
  }

  const endpointLabel = endpointParts.join(' + ');
  if (mobile && endpointLabel) return `${mobile} · ${endpointLabel}`;
  if (mobile) return mobile;
  if (endpointLabel) return endpointLabel;
  return '—';
}

function slugGroup(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function badgeFromGroupName(name: string): string {
  const brand = /mantech|^mt\b/i.test(name) ? 'MT' : /swan|^sw\b/i.test(name) ? 'SW' : '';
  const loc = (name.match(/\b(GP|WC|EC|KZN|CPT|PTA|JHB)\b/i) || [])[1];
  if (brand && loc) return `${brand}-${loc.toUpperCase()}`;
  if (loc) return loc.toUpperCase();
  return name
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 6);
}

const GROUP_COLORS = [
  '#0369a1',
  '#0d9488',
  '#7c3aed',
  '#be123c',
  '#b45309',
  '#0f766e',
  '#1d4ed8',
  '#9333ea',
  '#c2410c',
  '#334155',
];

function colorForGroup(name: string, index: number): string {
  const n = name.toLowerCase();
  if (/swan.*gp|gp.*swan/i.test(n)) return '#0369a1';
  if (/swan.*wc|wc.*swan/i.test(n)) return '#0d9488';
  if (/swan.*ec|ec.*swan/i.test(n)) return '#7c3aed';
  if (/swan.*kzn|kzn.*swan/i.test(n)) return '#be123c';
  if (/mantech|mt[\s-]/i.test(n)) return '#b45309';
  return GROUP_COLORS[index % GROUP_COLORS.length];
}

function parseGroupTokens(name: string): { brand: 'mantech' | 'swan' | 'other'; loc: string } {
  const brand = /mantech|\bmt\b/i.test(name)
    ? 'mantech'
    : /swan|\bsw\b/i.test(name)
      ? 'swan'
      : 'other';
  const loc = ((name.match(/\b(GP|WC|EC|KZN|CPT|PTA|JHB)\b/i) || [])[1] || '').toUpperCase();
  return { brand, loc };
}

function numberPrefix2(number: string): string {
  const digits = number.replace(/\D/g, '');
  return digits.length >= 2 ? digits.slice(0, 2) : digits;
}

function groupHasPbxMembers(group: ExtensionGroupInfo): boolean {
  if (!group.membershipAvailable) return false;
  return (
    (group.memberExtensionNumbers?.length ?? 0) > 0 ||
    (group.memberExtensionIds?.length ?? 0) > 0
  );
}

/**
 * Map extension groups → member extensions.
 *
 * Prefer real PBX membership (`memberExtensionNumbers` / IDs) when the
 * extension_group query exposes members. Only fall back to queue agents +
 * number-prefix heuristics for groups (or leftovers) without membership data.
 */
function assignExtensionsToGroups(
  extensions: Extension[],
  groups: ExtensionGroupInfo[],
  queues: QueueDetail[],
  _trunks: TrunkInfo[]
): { assignments: Map<string, string>; prefixByGroup: Map<string, string> } {
  const assignments = new Map<string, string>(); // ext number → group key
  const prefixScores = new Map<string, Map<string, number>>(); // prefix → groupKey → score
  const usableGroups = groups.filter((g) => !/^all\b/i.test(g.name));
  const extensionsById = new Map(extensions.map((ext) => [ext.id, ext]));

  const groupsWithMembers = usableGroups.filter(groupHasPbxMembers);
  const groupsNeedingHeuristic = usableGroups.filter((g) => !groupHasPbxMembers(g));

  // 1. Trust PBX extension-group membership (menu order = first-write wins on overlaps)
  for (const group of groupsWithMembers) {
    const key = slugGroup(group.name);
    for (const num of group.memberExtensionNumbers || []) {
      if (num && !assignments.has(num)) assignments.set(num, key);
    }
    for (const id of group.memberExtensionIds || []) {
      const ext = extensionsById.get(id);
      if (ext?.number && !assignments.has(ext.number)) {
        assignments.set(ext.number, key);
      }
    }
  }

  // 2. Heuristic fallback only for groups without PBX member lists
  for (const group of groupsNeedingHeuristic) {
    const key = slugGroup(group.name);
    const { brand, loc } = parseGroupTokens(group.name);

    const relatedQueues = queues.filter((q) => {
      const label = `${q.number || ''} ${q.name || ''}`;
      if (brand === 'mantech' && !/mantech|\bmt[\s_-]/i.test(label)) return false;
      if (brand === 'swan' && !/swan|\bsw[\s_-]/i.test(label)) return false;
      if (loc && !new RegExp(`\\b${loc}\\b`, 'i').test(label) && !new RegExp(loc, 'i').test(label)) {
        return false;
      }
      return brand !== 'other' || Boolean(loc);
    });

    for (const q of relatedQueues) {
      for (const a of q.agents) {
        const num = a.number;
        if (!num || num.length < 3) continue;
        if (!assignments.has(num)) assignments.set(num, key);
        const p = numberPrefix2(num);
        if (p.length === 2) {
          if (!prefixScores.has(p)) prefixScores.set(p, new Map());
          const m = prefixScores.get(p)!;
          m.set(key, (m.get(key) || 0) + 1);
        }
      }
    }

    // Soft prior for common multi-site numbering when we have location but few seeds
    if (loc) {
      const fallback: Record<string, Record<string, string>> = {
        mantech: { EC: '41', CPT: '21', KZN: '31', GP: '10' },
        swan: { EC: '42', WC: '22', KZN: '32', GP: '10' },
        other: {},
      };
      const prior = fallback[brand]?.[loc];
      if (prior) {
        if (!prefixScores.has(prior)) prefixScores.set(prior, new Map());
        const m = prefixScores.get(prior)!;
        // Weak prior — only wins if no stronger seed claim
        m.set(key, (m.get(key) || 0) + 0.5);
      }
    }
  }

  // Unique prefix owners expand to unassigned extensions with that prefix
  const prefixOwner = new Map<string, string>();
  for (const [prefix, scores] of prefixScores) {
    let bestKey = '';
    let best = 0;
    let second = 0;
    for (const [key, score] of scores) {
      if (score > best) {
        second = best;
        best = score;
        bestKey = key;
      } else if (score > second) {
        second = score;
      }
    }
    // Require a clear winner
    if (bestKey && best >= second + 0.4) prefixOwner.set(prefix, bestKey);
  }

  if (groupsNeedingHeuristic.length > 0) {
    for (const ext of extensions) {
      if (assignments.has(ext.number)) continue;
      const owner = prefixOwner.get(numberPrefix2(ext.number));
      if (owner) {
        assignments.set(ext.number, owner);
        continue;
      }

      const email = (ext.email || '').toLowerCase();
      let brand: 'mantech' | 'swan' | 'other' = 'other';
      if (email.includes('mantech')) brand = 'mantech';
      else if (email.includes('swan') || email.includes('swtech')) brand = 'swan';

      if (brand === 'other') continue;

      const brandGroups = groupsNeedingHeuristic.filter(
        (g) => parseGroupTokens(g.name).brand === brand
      );
      if (brandGroups.length === 1) {
        assignments.set(ext.number, slugGroup(brandGroups[0].name));
      }
    }
  }

  const prefixByGroup = new Map<string, string>();
  for (const [prefix, key] of prefixOwner) prefixByGroup.set(key, prefix);

  return { assignments, prefixByGroup };
}

function trunkHintForGroup(
  groupName: string,
  trunks: TrunkInfo[]
): string | undefined {
  const { brand, loc } = parseGroupTokens(groupName);
  const matched = trunks.filter((t) => {
    const n = t.name || '';
    if (brand === 'mantech' && !/mantech|\bmt[\s_-]/i.test(n)) return false;
    if (brand === 'swan' && !/swan|\bsw[\s_-]/i.test(n)) return false;
    if (loc && !new RegExp(loc, 'i').test(n)) return false;
    return brand !== 'other' || Boolean(loc);
  });
  if (!matched.length) return undefined;
  return matched.map((t) => t.name).join(' · ');
}

export async function buildExtensionDirectoryReport(
  customerName: string
): Promise<ExtensionDirectoryReport> {
  const [extensions, phones, groupInfos, trunks] = await Promise.all([
    fetchExtensions(false),
    fetchPhones(false),
    fetchExtensionGroups(false),
    fetchTrunks(false),
  ]);

  // Queue agents are only needed when PBX groups do not expose members.
  const needsHeuristic = groupInfos
    .filter((g) => !/^all\b/i.test(g.name))
    .some((g) => !groupHasPbxMembers(g));

  let queueDetails: QueueDetail[] = [];
  if (needsHeuristic) {
    const queues = await fetchQueues(false);
    queueDetails = (
      await Promise.all(queues.map((q) => fetchQueueDetail(q.id)))
    ).filter(Boolean) as QueueDetail[];
  }

  const { assignments } = assignExtensionsToGroups(
    extensions,
    groupInfos,
    queueDetails,
    trunks
  );

  const sorted = [...extensions].sort((a, b) =>
    a.number.localeCompare(b.number, undefined, { numeric: true })
  );

  const groupMeta = new Map<string, ExtensionGroupInfo>();
  groupInfos.forEach((g) => groupMeta.set(slugGroup(g.name), g));

  const rowsByGroup = new Map<string, ExtensionDirectoryRow[]>();
  let ungroupedCount = 0;

  for (const ext of sorted) {
    const groupKey = assignments.get(ext.number) || 'ungrouped';
    if (groupKey === 'ungrouped') ungroupedCount += 1;
    const row: ExtensionDirectoryRow = {
      number: ext.number,
      name: extensionDisplayName(ext),
      email: ext.email || '—',
      phone: phoneLabelForExt(ext, phones),
      groupKey,
    };
    const list = rowsByGroup.get(groupKey) || [];
    list.push(row);
    rowsByGroup.set(groupKey, list);
  }

  if (ungroupedCount > 0 && !rowsByGroup.has('ungrouped')) {
    // already set via loop
  }

  // Order: known groups as in menu, then ungrouped
  const orderedKeys: string[] = [
    ...groupInfos.map((g) => slugGroup(g.name)).filter((k) => rowsByGroup.has(k)),
    ...(rowsByGroup.has('ungrouped') ? ['ungrouped'] : []),
  ];
  // Include any assignment keys not in menu
  for (const key of rowsByGroup.keys()) {
    if (!orderedKeys.includes(key)) orderedKeys.push(key);
  }

  const groups: ExtensionDirectoryGroup[] = orderedKeys.map((key, index) => {
    const meta = groupMeta.get(key);
    const label =
      key === 'ungrouped' ? 'Other / ungrouped' : meta?.name || key.replace(/-/g, ' ');
    const badge = key === 'ungrouped' ? 'OTHER' : badgeFromGroupName(label);
    return {
      key,
      label,
      badge,
      color: key === 'ungrouped' ? '#64748b' : colorForGroup(label, index),
      phoneHint: meta ? trunkHintForGroup(meta.name, trunks) : undefined,
      rows: rowsByGroup.get(key) || [],
    };
  }).filter((g) => g.rows.length > 0);

  return {
    customerName,
    generatedAt: new Date().toISOString(),
    total: sorted.length,
    groups,
    ungroupedCount,
  };
}

function destKind(type: string): RouteDestSummary['kind'] {
  const t = type.toLowerCase();
  if (t === 'queue' || t.includes('queue')) return 'queue';
  if (t === 'ivr' || t.includes('ivr')) return 'ivr';
  if (t === 'ring_group' || t.includes('ring_group') || t.includes('ring group'))
    return 'ring_group';
  if (t === 'extension' || t === 'ext_vm' || t.startsWith('ext')) return 'extension';
  return 'other';
}

type Lookups = {
  extensionsById: Map<string, Extension>;
  extensionsByNumber: Map<string, Extension>;
  queuesById: Map<string, QueueDetail | { id: string; name: string; number?: string }>;
  queuesByNumber: Map<string, QueueDetail | { id: string; name: string; number?: string }>;
  queuesByName: Map<string, QueueDetail | { id: string; name: string; number?: string }>;
  ivrsById: Map<string, IvrDetail | { id: string; name: string; number?: string }>;
  ivrsByNumber: Map<string, IvrDetail | { id: string; name: string; number?: string }>;
  ivrsByName: Map<string, IvrDetail | { id: string; name: string; number?: string }>;
};

function normalizeKey(value: string): string {
  return value.trim().toLowerCase();
}

/** Format queue as "Name (number)" for reports and IVR menus. */
function formatQueueLabel(q: { name: string; number?: string }): string {
  if (q.name && q.number && q.name !== q.number) return `${q.name} (${q.number})`;
  if (q.number) return q.number;
  return q.name || 'Queue';
}

function formatExtensionLabel(ext: Extension): string {
  const name = extensionDisplayName(ext);
  if (name && name !== '—' && name !== ext.number) return `${name} (${ext.number})`;
  return ext.number;
}

function resolveDestLabel(type: string, value: string, lookups: Lookups): string {
  if (!type && !value) return 'Not set';
  const t = type.toLowerCase();
  if (!t || t === 'disabled') return 'Not set / disabled';
  if (t === 'end_call' || t.includes('hangup') || t === 'hang_up') return 'Hang up';
  if (t === 'play_greeting') return value ? `Play greeting (${value})` : 'Play greeting';
  if (t === 'play_prompt') return value ? `Play prompt (${value})` : 'Play prompt';
  if (t === 'external_num') return value ? `External ${value}` : 'External number';
  if (t === 'dial_by_name') return 'Dial by name';
  if (t === 'fax_to_email') return 'Fax to email';

  const kind = destKind(type);

  // Prefer ID lookup (OpenAPI get returns IDs); list responses sometimes return numbers/names.
  if (kind === 'extension') {
    const ext =
      lookups.extensionsById.get(value) || lookups.extensionsByNumber.get(value);
    if (ext) {
      const base = formatExtensionLabel(ext);
      return t === 'ext_vm' ? `Voicemail ${base}` : base;
    }
    return value ? `Extension ${value}` : 'Extension';
  }

  if (kind === 'queue') {
    const q =
      lookups.queuesById.get(value) ||
      lookups.queuesByNumber.get(value) ||
      lookups.queuesByName.get(normalizeKey(value));
    if (q) return formatQueueLabel(q as { name: string; number?: string });
    return value ? `Queue ${value}` : 'Queue';
  }

  if (kind === 'ivr') {
    const ivr =
      lookups.ivrsById.get(value) ||
      lookups.ivrsByNumber.get(value) ||
      lookups.ivrsByName.get(normalizeKey(value));
    if (ivr) {
      const named = ivr as { name: string; number?: string };
      if (named.name && named.number && named.name !== named.number) {
        return `${named.name} (${named.number})`;
      }
      return named.number || named.name;
    }
    return value ? `IVR ${value}` : 'IVR';
  }

  if (kind === 'ring_group') {
    return value ? `Ring group ${value}` : 'Ring group';
  }

  // Generic fallback for other destination types
  const byId =
    lookups.extensionsById.get(value) ||
    lookups.queuesById.get(value) ||
    lookups.ivrsById.get(value);
  if (byId) {
    if ('display_name' in byId || 'first_name' in byId) {
      return formatExtensionLabel(byId as Extension);
    }
    return formatQueueLabel(byId as { name: string; number?: string });
  }

  const byNumber =
    lookups.extensionsByNumber.get(value) ||
    lookups.queuesByNumber.get(value) ||
    lookups.ivrsByNumber.get(value);
  if (byNumber) {
    if ('display_name' in byNumber || 'first_name' in byNumber) {
      return formatExtensionLabel(byNumber as Extension);
    }
    return formatQueueLabel(byNumber as { name: string; number?: string });
  }

  if (!value) return type || '—';
  return type ? `${type} · ${value}` : value;
}

function summarizeDest(type: string, value: string, lookups: Lookups): RouteDestSummary {
  const kind = destKind(type);
  return {
    type: type || '—',
    value: value || '',
    label: resolveDestLabel(type, value, lookups),
    kind,
  };
}

function formatTrunks(route: InboundRoute, trunks: Map<string, TrunkInfo>): string {
  if (!route.trunk_list?.length) return 'All / unspecified trunks';
  return route.trunk_list
    .map((t) => {
      const id = String(t.id);
      const known = trunks.get(id);
      if (known) {
        const cid = known.defOutboundCid ? ` (${known.defOutboundCid})` : '';
        return `${known.name}${cid}`;
      }
      return t.name || `Trunk ${id}`;
    })
    .join(' · ');
}

function formatDids(route: InboundRoute): string {
  const bits: string[] = [];
  if (route.did_option === 'range_to_ext' && route.did_to_ext_start && route.did_to_ext_end) {
    bits.push(`${route.did_to_ext_start}–${route.did_to_ext_end}`);
  }
  if (route.did_pattern_to_ext) bits.push(route.did_pattern_to_ext);
  if (route.did_pattern_list?.length) {
    for (const item of route.did_pattern_list) {
      if (typeof item === 'string') bits.push(item);
      else if (item && typeof item === 'object') {
        const pattern = item.did_pattern || item.pattern;
        if (pattern) bits.push(String(pattern));
      }
    }
  }
  if (route.did_pattern_to_ext_list?.length) {
    for (const item of route.did_pattern_to_ext_list) {
      if (item.did_pattern) bits.push(item.did_pattern);
    }
  }
  if (route.did_list?.length) bits.push(...route.did_list.map(String));
  if (route.did_patterns) {
    if (Array.isArray(route.did_patterns)) bits.push(...route.did_patterns.map(String));
    else bits.push(String(route.did_patterns));
  }
  return bits.length ? Array.from(new Set(bits)).join(', ') : 'Any DID';
}

function formatTimeConditionMode(mode?: string): string {
  const m = (mode || 'disabled').toLowerCase();
  if (m === 'disabled' || !m) return 'Always (no time condition)';
  if (m === 'global') return 'Global business hours';
  if (m === 'route_scope') return 'Custom business hours (route)';
  if (m === 'custom') return 'Custom time periods';
  return mode || 'Unknown';
}

function formatSchedule(periods?: TimeConditionPeriod[]): string {
  if (!periods?.length) return '';
  return periods
    .map((period) => {
      const days = formatDaysOfWeek(period.days_of_week);
      const times = Array.isArray(period.times)
        ? period.times.join(', ')
        : period.times || '';
      return [days, times].filter(Boolean).join(' ');
    })
    .filter(Boolean)
    .join(', ');
}

/** "Global Time Zone[2 Central Africa Time]" → "Central Africa Time". */
function formatTimezone(timezone?: string): string {
  if (!timezone) return '';
  return timezone.replace(/^Global Time Zone\[?\d*\s*/i, '').replace(/\]$/, '').trim();
}

function isActiveDest(dest?: RouteDestinationInfo | null): boolean {
  if (!dest) return false;
  const type = (dest.type || '').toLowerCase();
  if (!type || type === 'disabled' || type === '—') {
    return Boolean(dest.value);
  }
  return true;
}

function findQueue(
  value: string,
  lookups: Lookups
): QueueDetail | { id: string; name: string; number?: string } | undefined {
  return (
    lookups.queuesById.get(value) ||
    lookups.queuesByNumber.get(value) ||
    lookups.queuesByName.get(normalizeKey(value))
  );
}

function findIvr(
  value: string,
  lookups: Lookups
): IvrDetail | { id: string; name: string; number?: string } | undefined {
  return (
    lookups.ivrsById.get(value) ||
    lookups.ivrsByNumber.get(value) ||
    lookups.ivrsByName.get(normalizeKey(value))
  );
}

function resolveIvrBranch(
  dest: RouteDestSummary,
  lookups: Lookups
): { ivr?: ResolvedIvr; queue?: QueueDetail } {
  if (dest.kind === 'queue') {
    const q = findQueue(dest.value, lookups);
    if (q && 'agents' in q) return { queue: q as QueueDetail };
    return {};
  }

  if (dest.kind !== 'ivr') return {};

  const detail = findIvr(dest.value, lookups);
  if (!detail || !('menu' in detail)) return {};

  const full = detail as IvrDetail;
  // A key with no destination type is switched off, even when a stale value remains.
  const menuResolved = full.menu.filter((opt) => opt.destType).map((opt) => {
    const resolvedLabel = resolveDestLabel(opt.destType, opt.destValue, lookups);
    const queueDetail =
      destKind(opt.destType) === 'queue'
        ? (findQueue(opt.destValue, lookups) as QueueDetail | undefined)
        : undefined;
    return {
      ...opt,
      resolvedLabel,
      queueDetail: queueDetail && 'agents' in (queueDetail || {}) ? queueDetail : undefined,
    };
  });

  return {
    ivr: {
      ...full,
      menuResolved,
      timeoutLabel:
        full.timeoutDestType || full.timeoutDestValue
          ? resolveDestLabel(full.timeoutDestType || '', full.timeoutDestValue || '', lookups)
          : undefined,
    },
  };
}

function buildBranch(
  title: string,
  destInfo: RouteDestinationInfo | undefined,
  lookups: Lookups
): DestinationBranch | undefined {
  if (!isActiveDest(destInfo)) return undefined;
  const dest = summarizeDest(destInfo!.type, destInfo!.value, lookups);
  if (dest.type === '—' && !dest.value) return undefined;
  const { ivr, queue } = resolveIvrBranch(dest, lookups);
  return { title, dest, ivr, queue };
}

/**
 * inbound_route/get often returns trunks + def_dest but OMITS time-condition
 * destinations that list already has. Merge list→get without wiping list fields.
 */
function mergeRouteDetails(listRoute: InboundRoute, getRoute: InboundRoute | null): InboundRoute {
  if (!getRoute) return listRoute;

  const prefer = <T>(getVal: T | undefined, listVal: T | undefined): T | undefined => {
    if (getVal === undefined || getVal === null || getVal === '') return listVal;
    if (typeof getVal === 'string' && getVal.toLowerCase() === 'disabled' && listVal) {
      // get often returns def_dest=disabled while list has real business-hours targets
      return listVal;
    }
    return getVal;
  };

  const merged: InboundRoute = {
    ...listRoute,
    ...getRoute,
    // Explicitly keep time-condition routing from list when get is sparse.
    // Newer firmware's get says time_condition "global" with enb_time_condition 0
    // when time conditions are switched off.
    time_condition:
      getRoute.enb_time_condition != null && Number(getRoute.enb_time_condition) === 0
        ? 'disabled'
        : prefer(getRoute.time_condition, listRoute.time_condition),
    time_condition_value: getRoute.time_condition_value?.length
      ? getRoute.time_condition_value
      : listRoute.time_condition_value,
    business_hours_destination: prefer(
      getRoute.business_hours_destination,
      listRoute.business_hours_destination
    ),
    business_hours_destination_value: prefer(
      getRoute.business_hours_destination_value,
      listRoute.business_hours_destination_value
    ),
    outside_business_hours_destination: prefer(
      getRoute.outside_business_hours_destination,
      listRoute.outside_business_hours_destination
    ),
    outside_business_hours_destination_value: prefer(
      getRoute.outside_business_hours_destination_value,
      listRoute.outside_business_hours_destination_value
    ),
    holidays_hours_destination: prefer(
      getRoute.holidays_hours_destination,
      listRoute.holidays_hours_destination
    ),
    holidays_hours_destination_value: prefer(
      getRoute.holidays_hours_destination_value,
      listRoute.holidays_hours_destination_value
    ),
    // Trunks: prefer get (usually complete)
    trunk_list: getRoute.trunk_list?.length ? getRoute.trunk_list : listRoute.trunk_list,
    // DID patterns: keep whichever has data
    did_option: prefer(getRoute.did_option, listRoute.did_option),
    did_pattern_list: getRoute.did_pattern_list?.length
      ? getRoute.did_pattern_list
      : listRoute.did_pattern_list,
    did_pattern_to_ext: prefer(getRoute.did_pattern_to_ext, listRoute.did_pattern_to_ext),
    did_to_ext_start: prefer(getRoute.did_to_ext_start, listRoute.did_to_ext_start),
    did_to_ext_end: prefer(getRoute.did_to_ext_end, listRoute.did_to_ext_end),
    // Default dest: list's default_destination when get only says disabled
    def_dest: prefer(getRoute.def_dest, listRoute.def_dest || listRoute.default_destination),
    def_dest_value: prefer(
      getRoute.def_dest_value,
      listRoute.def_dest_value ||
        listRoute.default_destination_value ||
        listRoute.default_desination_value
    ),
    default_destination: prefer(
      getRoute.default_destination,
      listRoute.default_destination
    ),
    default_destination_value: prefer(
      getRoute.default_destination_value,
      listRoute.default_destination_value
    ),
    default_desination_value: prefer(
      getRoute.default_desination_value,
      listRoute.default_desination_value
    ),
  };

  // timezone may only exist on list
  if ((listRoute as any).timezone && !(getRoute as any).timezone) {
    (merged as any).timezone = (listRoute as any).timezone;
  }

  // Re-run normalization on the merged payload
  const { normalizeInboundRouteForReport } = routeNormalizeHelpers;
  return normalizeInboundRouteForReport(merged);
}

/** Local re-normalize after merge (mirrors api.ts normalizeInboundRoute). */
const routeNormalizeHelpers = {
  normalizeInboundRouteForReport(route: InboundRoute): InboundRoute {
    // Importing normalize from api is private; re-apply via getInboundRoute path:
    // fetchInboundRoutes already normalizes list; getInboundRoute normalizes get.
    // We recompute normalized_* from merged fields using the same pick logic.
    const pick = (...vals: unknown[]) => {
      for (const v of vals) {
        const s = v === null || v === undefined ? '' : String(v).trim();
        if (s && s.toLowerCase() !== 'disabled') return s;
      }
      for (const v of vals) {
        if (v !== undefined && v !== null) return String(v).trim();
      }
      return '';
    };
    const defType = pick(route.def_dest, route.default_destination, route.destination);
    const defValue = pick(
      route.def_dest_value,
      route.default_destination_value,
      route.default_desination_value,
      route.destination_value
    );
    const bizType = pick(route.business_hours_destination);
    const bizValue = pick(route.business_hours_destination_value);
    const outType = pick(route.outside_business_hours_destination);
    const outValue = pick(route.outside_business_hours_destination_value);
    const holType = pick(route.holidays_hours_destination);
    const holValue = pick(route.holidays_hours_destination_value);
    const tc = (route.time_condition || 'disabled').toLowerCase();

    let route_mode: InboundRoute['route_mode'] = 'standard_editable';
    if (tc && tc !== 'disabled') {
      if (tc === 'custom') route_mode = 'time_condition_custom_unsupported';
      else if (tc === 'global' || tc === 'route_scope') route_mode = 'time_condition_global_default';
      else route_mode = 'unknown_capability';
    } else if (bizType || outType || holType) {
      route_mode = 'time_condition_global_default';
    }

    return {
      ...route,
      route_mode,
      normalized_default_destination: { type: defType, value: defValue, prefix: '' },
      normalized_business_hours_destination: { type: bizType, value: bizValue, prefix: '' },
      normalized_outside_business_hours_destination: {
        type: outType,
        value: outValue,
        prefix: '',
      },
      normalized_holidays_destination: { type: holType, value: holValue, prefix: '' },
    };
  },
};

function branchPrefixFromRouteName(name: string): string | null {
  // In-Mantech-EC / In-MT-EC / In-Swan-GP → MT-EC / SW-GP style
  const cleaned = name.replace(/^In[-_]?/i, '');
  const m = cleaned.match(/^(MT|Mantech|SW|Swan)[-_]?([A-Z]{2,3})/i);
  if (!m) return null;
  const brand = /mantech|^mt$/i.test(m[1]) ? 'MT' : 'SW';
  return `${brand}-${m[2].toUpperCase()}`;
}

function formatScheduleLabel(
  mode: string,
  periods?: TimeConditionPeriod[],
  timezone?: string
): string {
  const schedule = formatSchedule(periods);
  if (schedule) {
    const tz = timezone ? ` (${formatTimezone(timezone)})` : '';
    return schedule + tz;
  }
  if (mode === 'global') return 'PBX global business hours';
  return '';
}

/** Days of call history used to work out custom time-period destinations. */
const HISTORY_DAYS = 7;

/** List-style "6202-IVR-ProshopHours" → "6202"; greeting files and external numbers stay as they are. */
function destValueKey(type: string, value?: string): string {
  const t = type.toLowerCase();
  if (t === 'play_greeting' || t === 'play_prompt' || t === 'external_num') return (value || '').trim();
  return currentDestinationKey(value);
}

/** Where one recent call in a custom time period went first. */
type PeriodCall = { day: string; type: string; value: string };

/** Destination of a CDR first leg ("IVR IVR-ProshopHours<6202>"), resolved by number. */
function destFromCallTo(row: CallRecord, lookups: Lookups): { type: string; value: string } | null {
  const number = (row.call_to_number || row.call_to?.match(/<([^>]+)>\s*$/)?.[1] || '').trim();
  if (!number) return null;
  const ivr = lookups.ivrsByNumber.get(number);
  if (ivr) return { type: 'ivr', value: ivr.id };
  const queue = lookups.queuesByNumber.get(number);
  if (queue) return { type: 'queue', value: queue.id };
  const ext = lookups.extensionsByNumber.get(number);
  if (ext) {
    return { type: /^voicemail\b/i.test(row.call_to || '') ? 'ext_vm' : 'extension', value: ext.id };
  }
  if (/^ring\s*group\b/i.test(row.call_to || '')) return { type: 'ring_group', value: number };
  return null;
}

function unknownBranch(title: string): DestinationBranch {
  return {
    title,
    dest: { type: '', value: '', label: 'Not reported by the PBX', kind: 'other' },
    unknown: true,
    note: "The PBX's API does not give this destination and no recent calls fell in this time. Check it in the PBX's inbound route settings.",
  };
}

function topDestination(calls: PeriodCall[]): { type: string; value: string; count: number } {
  const tally = new Map<string, { type: string; value: string; count: number }>();
  for (const call of calls) {
    const key = `${call.type}:${call.value}`;
    const entry = tally.get(key);
    if (entry) entry.count += 1;
    else tally.set(key, { type: call.type, value: call.value, count: 1 });
  }
  return Array.from(tally.values()).sort((a, b) => b.count - a.count)[0];
}

/**
 * Routing is fixed by the setup, so calls in one period going somewhere else on
 * different days means the setup changed: go by the most recent day's calls.
 */
function historyBranch(
  title: string,
  calls: PeriodCall[] | undefined,
  lookups: Lookups
): DestinationBranch | undefined {
  if (!calls?.length) return undefined;
  const lastDay = calls.reduce((latest, c) => (c.day > latest ? c.day : latest), '');
  const recent = calls.filter((c) => c.day === lastDay);
  const top = topDestination(recent);
  const branch = buildBranch(title, { type: top.type, value: top.value, prefix: '' }, lookups);
  if (!branch) return undefined;
  const earlier = calls.filter((c) => c.day < lastDay);
  const before = earlier.length ? topDestination(earlier) : null;
  const changed = before && (before.type !== top.type || before.value !== top.value);
  return {
    ...branch,
    fromHistory: true,
    note:
      `Not reported by the PBX, so taken from the last calls in this time (${lastDay}): ${top.count} of ${recent.length} went here.` +
      (changed ? ` Before that, calls went to ${resolveDestLabel(before.type, before.value, lookups)}.` : '') +
      ' A change made since then will not show until a call comes in at this time.',
  };
}

/**
 * time_condition "custom": the PBX checks its time periods top to bottom and
 * sends the call to the first match's destination. Some firmware (P-Series
 * Cloud 84.x) leaves the per-period destinations out of the API, so fall back
 * to where calls go right now, then to recent call history.
 */
function buildCustomPeriodLanes(
  route: InboundRoute,
  lookups: Lookups,
  clock: PbxClock | null,
  history: Map<number, PeriodCall[]> | undefined
): DestinationBranch[] {
  const periods = route.time_condition_value || [];
  const active = activePeriodIndex(periods, clock);
  const currentType = (route.current_destination || '').trim();
  const currentValue = destValueKey(
    currentType,
    route.current_desination_value || route.current_destination_value
  );
  const liveBranch = (title: string): DestinationBranch | undefined => {
    if (!currentType) return undefined;
    const branch = buildBranch(title, { type: currentType, value: currentValue, prefix: '' }, lookups);
    return branch && { ...branch, live: true, note: 'The PBX is sending calls here right now.' };
  };

  const lanes = periods.map((period, i): DestinationBranch => {
    const title = formatPeriod(period);
    if (period.dest) {
      // List responses give "6203-SW-EC-AfterHours"; get gives the id.
      const branch = buildBranch(
        title,
        { type: period.dest, value: destValueKey(period.dest, period.dest_value), prefix: period.dest_prefix || '' },
        lookups
      );
      if (branch) return { ...branch, live: active === i };
    }
    return (
      (active === i ? liveBranch(title) : undefined) ||
      historyBranch(title, history?.get(i), lookups) ||
      unknownBranch(title)
    );
  });

  // Outside every period the PBX uses the route's outside-business-hours destination.
  const otherTitle = 'Any other time';
  const outsideType = (route.outside_business_hours_destination || '').trim();
  const outside =
    outsideType && outsideType.toLowerCase() !== 'disabled'
      ? buildBranch(
          otherTitle,
          {
            type: outsideType,
            value: destValueKey(outsideType, route.outside_business_hours_destination_value),
            prefix: route.outside_business_hours_destination_prefix || '',
          },
          lookups
        )
      : undefined;
  const defaultBranch = buildBranch(otherTitle, route.normalized_default_destination, lookups);
  const other =
    (active === -1 ? liveBranch(otherTitle) : undefined) ||
    (outside && { ...outside, live: false }) ||
    historyBranch(otherTitle, history?.get(-1), lookups) ||
    (defaultBranch && { ...defaultBranch, note: "The route's default destination setting." });
  if (other) lanes.push({ ...other, outsidePeriods: true });

  const holidayType = (route.holidays_hours_destination || '').trim();
  if (holidayType && holidayType.toLowerCase() !== 'disabled') {
    const holidays = buildBranch('Holidays', route.normalized_holidays_destination, lookups);
    if (holidays) lanes.push({ ...holidays, outsidePeriods: true });
  }
  return lanes;
}

/** Trunk names a route listens on, or null for all trunks. */
function routeTrunkNames(route: InboundRoute, trunks: Map<string, TrunkInfo>): Set<string> | null {
  if (!route.trunk_list?.length) return null;
  const names = new Set<string>();
  for (const t of route.trunk_list) {
    if (t.name) names.add(t.name);
    const known = trunks.get(String(t.id));
    if (known?.name) names.add(known.name);
  }
  return names;
}

/** "2026-10-23" for a CDR row, read in the PBX's date format. */
function pbxDay(leg: CallRecord, dateFormat = 'YYYY/MM/DD'): string {
  const m = (leg.time || '').trim().match(/^(\d+)\D(\d+)\D(\d+)/);
  if (!m) return '';
  const order = dateFormat.toUpperCase().split(/[^A-Z]+/).filter(Boolean);
  const parts: Record<string, string> = {};
  order.forEach((token, i) => (parts[token[0]] = m[i + 1]));
  if (!parts.Y || !parts.M || !parts.D) return `${m[1]}-${m[2]}-${m[3]}`;
  return `${parts.Y}-${String(parts.M).padStart(2, '0')}-${String(parts.D).padStart(2, '0')}`;
}

/**
 * Where recent calls went per custom time period (-1 = no period matched),
 * keyed by route id. A call counts only when exactly one route listens on its trunk.
 */
function tallyCustomRouteHistory(
  routes: InboundRoute[],
  customRoutes: InboundRoute[],
  legs: CallRecord[],
  trunks: Map<string, TrunkInfo>,
  lookups: Lookups,
  dateFormat?: string
): Map<number, Map<number, PeriodCall[]>> {
  const result = new Map<number, Map<number, PeriodCall[]>>();
  const listeners = routes.map((route) => ({ route, trunks: routeTrunkNames(route, trunks) }));
  const customIds = new Set(customRoutes.map((r) => r.id));

  for (const leg of legs) {
    const matching = listeners.filter(
      (l) => !l.trunks || (leg.src_trunk ? l.trunks.has(leg.src_trunk) : false)
    );
    if (matching.length !== 1 || !customIds.has(matching[0].route.id)) continue;
    const route = matching[0].route;
    const clock = parsePbxClock(leg.time, dateFormat);
    const index = activePeriodIndex(route.time_condition_value || [], clock);
    if (index === null) continue;
    const dest = destFromCallTo(leg, lookups);
    if (!dest) continue;

    let byPeriod = result.get(route.id);
    if (!byPeriod) result.set(route.id, (byPeriod = new Map()));
    let calls = byPeriod.get(index);
    if (!calls) byPeriod.set(index, (calls = []));
    // CDR "time" is the PBX's local wall clock; ISO-style so days sort as text
    calls.push({ ...dest, day: pbxDay(leg, dateFormat) });
  }
  return result;
}

/** IVR ids any branch reaches, directly or from another IVR's menu. */
function referencedIvrIds(branches: DestinationBranch[], lookups: Lookups): Set<string> {
  const ids = new Set<string>();
  const addDest = (type: string, value: string) => {
    if (destKind(type) !== 'ivr' || !value) return;
    const ivr = findIvr(value, lookups);
    if (ivr) ids.add(ivr.id);
  };
  for (const branch of branches) {
    if (!branch.ivr) continue;
    ids.add(branch.ivr.id);
    for (const opt of branch.ivr.menu) addDest(opt.destType, opt.destValue);
    addDest(branch.ivr.timeoutDestType || '', branch.ivr.timeoutDestValue || '');
  }
  return ids;
}

export async function buildInboundRoutingReport(
  customerName: string
): Promise<InboundRoutingReport> {
  const [routeList, extensions, queues, ivrs, trunks, pbxTime] = await Promise.all([
    fetchInboundRoutes(),
    fetchExtensions(false),
    fetchQueues(false),
    fetchIVRs(false),
    fetchTrunks(false),
    fetchPbxSystemTime(),
  ]);
  const clock = parsePbxClock(pbxTime?.systemTime, pbxTime?.dateFormat);

  // Hydrate trunks/DIDs from get, but MERGE so list time-condition fields survive.
  const detailedRoutes = (
    await Promise.all(
      routeList.map(async (route) => {
        try {
          const full = await getInboundRoute(route.id);
          return mergeRouteDetails(route, full);
        } catch {
          return route;
        }
      })
    )
  ).filter(Boolean) as InboundRoute[];

  const extMap = new Map(extensions.map((e) => [e.id, e]));
  const extByNumber = new Map(extensions.map((e) => [e.number, e]));
  const queueMap = new Map(queues.map((q) => [q.id, q]));
  const queueByNumber = new Map(
    queues.filter((q) => q.number).map((q) => [q.number as string, q])
  );
  const queueByName = new Map(queues.map((q) => [normalizeKey(q.name), q]));
  const ivrMap = new Map(ivrs.map((i) => [i.id, i]));
  const ivrByNumber = new Map(
    ivrs.filter((i) => i.number).map((i) => [i.number as string, i])
  );
  const ivrByName = new Map(ivrs.map((i) => [normalizeKey(i.name), i]));
  const trunkMap = new Map(trunks.map((t) => [t.id, t]));

  // Load ALL IVR details (menus) + ALL queue details (agents) — needed for a complete report.
  const [ivrDetails, queueDetails] = await Promise.all([
    Promise.all(ivrs.map((i) => fetchIvrDetail(i.id))).then(
      (rows) => rows.filter(Boolean) as IvrDetail[]
    ),
    Promise.all(queues.map((q) => fetchQueueDetail(q.id))).then(
      (rows) => rows.filter(Boolean) as QueueDetail[]
    ),
  ]);

  for (const q of queueDetails) {
    queueMap.set(q.id, q);
    if (q.number) queueByNumber.set(q.number, q);
    queueByName.set(normalizeKey(q.name), q);
  }
  for (const i of ivrDetails) {
    ivrMap.set(i.id, i);
    if (i.number) ivrByNumber.set(i.number, i);
    ivrByName.set(normalizeKey(i.name), i);
  }

  const lookups: Lookups = {
    extensionsById: extMap,
    extensionsByNumber: extByNumber,
    queuesById: queueMap,
    queuesByNumber: queueByNumber,
    queuesByName: queueByName,
    ivrsById: ivrMap,
    ivrsByNumber: ivrByNumber,
    ivrsByName: ivrByName,
  };

  // Resolve queue failover destinations (API returns internal IDs, not ext numbers)
  for (const q of queueDetails) {
    if (q.failDestType || q.failDestValue) {
      q.failDestLabel = resolveDestLabel(
        q.failDestType || '',
        q.failDestValue || '',
        lookups
      );
    }
  }

  const customRoutes = detailedRoutes.filter(
    (route) => (route.time_condition || '').toLowerCase() === 'custom'
  );
  const customHistory = customRoutes.length
    ? tallyCustomRouteHistory(
        detailedRoutes,
        customRoutes,
        await fetchRecentInboundFirstLegs(HISTORY_DAYS),
        trunkMap,
        lookups,
        pbxTime?.dateFormat
      )
    : new Map<number, Map<number, PeriodCall[]>>();

  const cards: InboundRouteReportCard[] = detailedRoutes.map((route) => {
    const mode = (route.time_condition || 'disabled').toLowerCase();
    const timezone = String((route as any).timezone || '');
    const schedule = formatScheduleLabel(mode, route.time_condition_value, timezone);
    const timeConditionActive = Boolean(mode && mode !== 'disabled');
    const periodLanes =
      mode === 'custom'
        ? buildCustomPeriodLanes(route, lookups, clock, customHistory.get(route.id))
        : undefined;

    const always = buildBranch(
      'Default destination',
      route.normalized_default_destination,
      lookups
    );
    const office = buildBranch(
      'During office hours',
      route.normalized_business_hours_destination,
      lookups
    );
    const after = buildBranch(
      'After hours / outside business hours',
      route.normalized_outside_business_hours_destination,
      lookups
    );
    // Only show holidays as separate lane if destination differs from after-hours
    const holidayRaw = route.normalized_holidays_destination;
    const afterRaw = route.normalized_outside_business_hours_destination;
    const holidayDistinct =
      holidayRaw &&
      isActiveDest(holidayRaw) &&
      (holidayRaw.type !== afterRaw?.type || holidayRaw.value !== afterRaw?.value);
    const holidays = holidayDistinct
      ? buildBranch('Holidays', holidayRaw, lookups)
      : undefined;

    // Queues on the office IVR menu
    const menuQueueIds = new Set<string>();
    for (const branch of periodLanes || [office, always]) {
      if (!branch?.ivr) continue;
      for (const opt of branch.ivr.menuResolved) {
        if (opt.queueDetail) menuQueueIds.add(opt.queueDetail.id);
        if (destKind(opt.destType) === 'queue' && opt.destValue) {
          const q = findQueue(opt.destValue, lookups);
          if (q) menuQueueIds.add(q.id);
        }
      }
    }

    const prefix = branchPrefixFromRouteName(route.name || '');
    const otherQueues = prefix
      ? queueDetails.filter((q) => {
          if (menuQueueIds.has(q.id)) return false;
          const label = `${q.number || ''} ${q.name || ''}`;
          // Match MT-EC / SW-GP style prefixes in queue names
          const compact = prefix.replace('-', '[-_ ]?');
          return new RegExp(compact, 'i').test(label) || new RegExp(prefix.replace('-', ''), 'i').test(label);
        })
      : [];

    return {
      id: route.id,
      name: route.name || `Route ${route.id}`,
      trunksLabel: formatTrunks(route, trunkMap),
      didsLabel: formatDids(route),
      timeConditionMode: formatTimeConditionMode(route.time_condition),
      // Custom periods each get their own branch, so no one-line "office hours".
      scheduleLabel: periodLanes ? '' : schedule,
      timezoneLabel: formatTimezone(timezone) || undefined,
      alwaysDest: timeConditionActive ? undefined : always,
      officeHours: timeConditionActive && !periodLanes ? office || always : undefined,
      afterHours: timeConditionActive && !periodLanes ? after : undefined,
      holidays: timeConditionActive && !periodLanes ? holidays : undefined,
      periodLanes,
      otherQueues,
      routeMode: route.route_mode,
    };
  });

  const allBranches = cards.flatMap((card) =>
    [card.alwaysDest, card.officeHours, card.afterHours, card.holidays, ...(card.periodLanes || [])]
      .filter(Boolean) as DestinationBranch[]
  );
  const reached = referencedIvrIds(allBranches, lookups);
  const unlinkedIvrs = allBranches.some((b) => b.unknown)
    ? ivrDetails
        .filter((ivr) => !reached.has(ivr.id))
        .map((ivr) => resolveIvrBranch(summarizeDest('ivr', ivr.id, lookups), lookups).ivr)
        .filter((ivr): ivr is ResolvedIvr => Boolean(ivr))
    : [];

  return {
    customerName,
    generatedAt: new Date().toISOString(),
    cards,
    unlinkedIvrs,
    queues: queueDetails,
    ivrs: ivrDetails,
  };
}

export function downloadHtml(filename: string, html: string) {
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function openPrintableHtml(html: string) {
  const win = window.open('', '_blank');
  if (!win) return;
  win.document.open();
  win.document.write(html);
  win.document.close();
}

const REPORT_CSS_BASE = `
  :root{
    --ink:#1f2933; --muted:#6b7280; --line:#d6dde5; --bg:#f4f6f9;
    --card:#ffffff; --accent:#2563eb; --office:#0f766e; --after:#9a6700;
    --queue:#1d4ed8; --ext:#7c3aed; --warn:#b91c1c; --warnbg:#fef2f2;
  }
  *{box-sizing:border-box}
  body{margin:0;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;
       color:var(--ink);background:var(--bg);line-height:1.45;-webkit-print-color-adjust:exact;print-color-adjust:exact;}
  .wrap{max-width:1040px;margin:0 auto;padding:32px 24px 64px;}
  header.top{border-bottom:3px solid var(--accent);padding-bottom:18px;margin-bottom:24px;}
  header.top h1{margin:0 0 4px;font-size:26px;letter-spacing:-.3px;}
  header.top .sub{color:var(--muted);font-size:14px;}
  header.top .meta{margin-top:10px;font-size:12.5px;color:var(--muted);}
  .footer{margin-top:30px;border-top:1px solid var(--line);padding-top:16px;font-size:12px;color:var(--muted);}
  @media print{ body{background:#fff} .wrap{padding:0} .no-print{display:none !important} }
`;

export function renderExtensionDirectoryHtml(report: ExtensionDirectoryReport): string {
  const groupsHtml = report.groups
    .map((group) => {
      const rows = group.rows
        .map(
          (row) => `
        <tr>
          <td><span class="ext">${escapeHtml(row.number)}</span></td>
          <td class="nm">${escapeHtml(row.name)}</td>
          <td class="em${row.email === '—' ? ' off' : ''}">${escapeHtml(row.email)}</td>
          <td class="ph">${escapeHtml(row.phone)}</td>
        </tr>`
        )
        .join('');
      return `
      <section class="branch" id="${escapeHtml(group.key)}">
        <div class="bhead" style="background:${escapeHtml(group.color)}">
          <span class="badge">${escapeHtml(group.badge)}</span>
          <h2>${escapeHtml(group.label)}</h2>
          <span class="cnt">${group.rows.length} extensions</span>
          ${group.phoneHint ? `<span class="num">${escapeHtml(group.phoneHint)}</span>` : ''}
        </div>
        <table>
          <thead><tr><th style="width:64px">Ext</th><th>Name</th><th>Email</th><th>Phone</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </section>`;
    })
    .join('\n');

  const filterHtml = report.groups
    .map(
      (g) =>
        `<button type="button" data-filter="${escapeHtml(g.key)}" style="background:${escapeHtml(
          g.color
        )}">${escapeHtml(g.badge)} <span class="c">· ${g.rows.length}</span></button>`
    )
    .join('');

  const updated = new Date(report.generatedAt).toLocaleString();

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1.0"/>
<title>${escapeHtml(report.customerName)} — Extension Directory</title>
<style>
${REPORT_CSS_BASE}
  .filters{display:flex;flex-wrap:wrap;gap:10px;margin:0 0 24px;}
  .filters button{border:0;cursor:pointer;font-size:12.5px;font-weight:700;color:#fff;padding:7px 12px;border-radius:20px;
                  font-family:inherit;box-shadow:0 1px 2px rgba(16,24,40,.12);opacity:.9;}
  .filters button .c{opacity:.85;font-weight:500;}
  .filters .all{background:#111827;}
  .branch{background:var(--card);border:1px solid var(--line);border-radius:14px;
          box-shadow:0 1px 3px rgba(16,24,40,.06);margin-bottom:24px;overflow:hidden;break-inside:avoid;}
  .bhead{padding:15px 20px;display:flex;flex-wrap:wrap;align-items:center;gap:8px 14px;color:#fff;}
  .bhead .badge{font-weight:700;font-size:12px;background:rgba(255,255,255,.22);padding:3px 9px;border-radius:5px;letter-spacing:.5px;}
  .bhead h2{margin:0;font-size:18px;flex:1 1 auto;}
  .bhead .cnt{font-size:12.5px;opacity:.92;}
  .bhead .num{font-size:12.5px;font-weight:600;opacity:.95;}
  table{width:100%;border-collapse:collapse;font-size:13px;}
  thead th{text-align:left;font-size:10.5px;letter-spacing:.5px;text-transform:uppercase;color:var(--muted);
           padding:9px 14px;border-bottom:1px solid var(--line);background:#fafbfc;font-weight:700;}
  tbody td{padding:9px 14px;border-bottom:1px solid #f1f4f8;vertical-align:top;}
  tbody tr:nth-child(even){background:#fafbfc;}
  .ext{font-weight:700;font-variant-numeric:tabular-nums;background:#eef2ff;border-radius:6px;padding:2px 8px;display:inline-block;}
  .nm{font-weight:600;} .em{color:#334155;font-size:12px;word-break:break-all;}
  .em.off{color:#9aa3af;font-style:italic;} .ph{color:#475569;font-size:12px;font-variant-numeric:tabular-nums;}
  @media print{ .filters{display:none} }
</style>
</head>
<body>
<div class="wrap">
  <header class="top">
    <h1>Extension Directory — ${escapeHtml(report.customerName)}</h1>
    <div class="sub">Phone extensions by department / company (from PBX extension groups).</div>
    <div class="meta">Yeastar P-Series &nbsp;&bull;&nbsp; ${report.total} extensions &nbsp;&bull;&nbsp; Generated ${escapeHtml(updated)}</div>
  </header>
  <nav class="filters" aria-label="Filter by group">
    <button type="button" class="all" data-filter="all">All · ${report.total}</button>
    ${filterHtml}
  </nav>
  ${groupsHtml}
  <div class="footer">
    <p>Grouped from PBX extension-group membership (queue/prefix heuristics only when the group API does not expose members). Phone shows mobile number (when set) plus registered handset model or Linkus App.</p>
  </div>
</div>
</body>
</html>`;
}

/** What the greeting says a key is for, quoted unless it is a note like "(not heard…)". */
export function quoteSaid(said: string): string {
  return said.startsWith('(') ? said : `“${said}”`;
}

function renderGreetingHtml(greeting: IvrGreeting): string {
  const source =
    greeting.source === 'prompt'
      ? 'From the greeting file on the PBX'
      : greeting.source === 'recording'
        ? `From a recorded call on ${greeting.recordedAt || 'a recent day'}`
        : '';
  const check = greeting.check;
  const problems = check
    ? [...check.keys.filter((k) => !k.ok && k.note).map((k) => `Key ${k.key}: ${k.note}`), ...check.notes]
    : [];
  return `<div class="greet">
      <div class="gh">What callers hear</div>
      ${greeting.transcript ? `<div class="gt">“${escapeHtml(greeting.transcript)}”</div>` : ''}
      ${greeting.message ? `<div class="gm">${escapeHtml(greeting.message)}</div>` : ''}
      ${source ? `<div class="gs">${escapeHtml(source)}</div>` : ''}
      ${
        check
          ? `<div class="gc ${check.ok ? 'ok' : 'warn'}"><b>${
              check.ok ? '✓ The keys match the greeting' : '⚠ Check this menu'
            }</b>${problems.map((line) => `<div>${escapeHtml(line)}</div>`).join('')}</div>`
          : ''
      }
    </div>`;
}

function renderBranchHtml(
  branch: DestinationBranch,
  laneClass: 'office' | 'after' | 'holiday' | 'unknown',
  greetings?: Map<string, IvrGreeting>
): string {
  const key = greetingKey(branch);
  const greeting = key ? greetings?.get(key) : undefined;
  const saidByKey = new Map((greeting?.check?.keys || []).map((k) => [k.key, k]));
  const menuHtml =
    branch.ivr?.menuResolved?.length
      ? `<div class="menu">${branch.ivr.menuResolved
          .map((opt) => {
            const isQueue = opt.destType.toLowerCase().includes('queue');
            const q = opt.queueDetail;
            let members = '';
            if (q) {
              const agents =
                q.agents?.length
                  ? `<div class="members">${escapeHtml(
                      q.agents
                        .map((a) => (a.name ? `${a.number} ${a.name}` : a.number))
                        .join(', ')
                    )}</div>`
                  : '';
              const failover =
                q.failDestLabel || q.failDestType
                  ? ` · failover → ${escapeHtml(
                      q.failDestLabel ||
                        [q.failDestType, q.failDestValue].filter(Boolean).join(' ')
                    )}`
                  : '';
              members = `${agents}<div class="qmeta"><b>${escapeHtml(
                q.ringStrategy || 'Ring'
              )}</b>${
                q.maxWaitTime != null
                  ? ` · up to ${escapeHtml(String(q.maxWaitTime))} sec`
                  : ''
              }${failover}</div>`;
            }
            return `
            <div class="opt">
              <div class="key">${escapeHtml(opt.key)}</div>
              <div class="dest">
                <div class="t"><span class="tag ${isQueue ? 'q' : 'e'}">${escapeHtml(
                  isQueue ? 'QUEUE' : (opt.destType || 'DEST').toUpperCase()
                )}</span>${escapeHtml(opt.resolvedLabel)}</div>
                ${
                  saidByKey.get(opt.key)?.said
                    ? `<div class="said ${saidByKey.get(opt.key)!.ok ? 'ok' : 'warn'}">${
                        saidByKey.get(opt.key)!.ok ? '✓' : '⚠'
                      } Greeting: ${escapeHtml(quoteSaid(saidByKey.get(opt.key)!.said))}</div>`
                    : ''
                }
                ${members}
              </div>
            </div>`;
          })
          .join('')}</div>`
      : '';

  const timeoutHtml =
    branch.ivr?.timeoutLabel
      ? `<div class="afterbox">No-input / timeout → ${escapeHtml(branch.ivr.timeoutLabel)}</div>`
      : '';

  const queueMeta =
    branch.queue
      ? `<div class="qmeta"><b>${escapeHtml(branch.queue.ringStrategy || 'Ring strategy n/a')}</b>${
          branch.queue.maxWaitTime != null
            ? ` · up to ${escapeHtml(String(branch.queue.maxWaitTime))} sec`
            : ''
        }</div>
        <div class="members">${
          branch.queue.agents.length
            ? escapeHtml(
                branch.queue.agents
                  .map((a) => (a.name ? `${a.number} ${a.name}` : a.number))
                  .join(', ')
              )
            : 'No agents listed'
        }</div>`
      : '';

  const ivrMeta = branch.ivr
    ? `<span class="vn">IVR ${escapeHtml(branch.ivr.number || branch.ivr.id)} · ${escapeHtml(
        branch.ivr.name
      )}</span>`
    : branch.unknown
      ? ''
      : `<span class="vn">${escapeHtml(branch.dest.type)}${
          branch.dest.value ? ` · ${escapeHtml(branch.dest.value)}` : ''
        }</span>`;

  return `
    <div class="lane ${laneClass}">
      <div class="lanehd">${escapeHtml(branch.title)}${
        branch.live ? ' <span class="live">Live now</span>' : ''
      }${branch.fromHistory ? ' <span class="hist">From call history</span>' : ''}</div>
      <div class="arrow small"></div>
      <div class="ivr ${laneClass === 'office' || laneClass === 'unknown' ? laneClass : 'after'}">${escapeHtml(branch.dest.label)}
        ${ivrMeta}
      </div>
      ${branch.note ? `<div class="note">${escapeHtml(branch.note)}</div>` : ''}
      ${greeting ? renderGreetingHtml(greeting) : ''}
      ${menuHtml}
      ${timeoutHtml}
      ${queueMeta}
    </div>`;
}

export function renderInboundRoutingHtml(
  report: InboundRoutingReport,
  greetings?: Map<string, IvrGreeting>
): string {
  const cards = report.cards
    .map((card) => {
      const branches: Array<{
        branch: DestinationBranch;
        lane: 'office' | 'after' | 'holiday' | 'unknown';
      }> = [];
      if (card.alwaysDest) branches.push({ branch: card.alwaysDest, lane: 'office' });
      if (card.officeHours) branches.push({ branch: card.officeHours, lane: 'office' });
      if (card.afterHours) branches.push({ branch: card.afterHours, lane: 'after' });
      if (card.holidays) branches.push({ branch: card.holidays, lane: 'holiday' });
      for (const branch of card.periodLanes || []) {
        branches.push({
          branch,
          lane: branch.unknown ? 'unknown' : branch.outsidePeriods ? 'after' : 'office',
        });
      }

      const hasTimeSplit = Boolean(
        card.officeHours || card.afterHours || card.holidays || card.periodLanes
      );
      const tcLabel = card.periodLanes
        ? 'Which time period is it?'
        : hasTimeSplit
          ? 'Office hours or after hours?'
          : card.alwaysDest
            ? 'Destination'
            : 'No destination configured';
      const tcSub = card.periodLanes
        ? ['First match from left to right', card.timezoneLabel].filter(Boolean).join(' · ')
        : card.scheduleLabel || card.timeConditionMode;

      return `
      <section class="company">
        <div class="chead">
          <span class="badge">ROUTE</span>
          <h2>${escapeHtml(card.name)}</h2>
        </div>
        <div class="cfacts">
          <span>Trunks: <b>${escapeHtml(card.trunksLabel)}</b></span>
          <span>DIDs: <b>${escapeHtml(card.didsLabel)}</b></span>
          <span>Time mode: <b>${escapeHtml(card.timeConditionMode)}</b></span>
          ${
            card.scheduleLabel
              ? `<span>Office hours: <b>${escapeHtml(card.scheduleLabel)}</b></span>`
              : ''
          }
        </div>
        <div class="flow">
          <div class="node incoming">Incoming call → ${escapeHtml(
            card.trunksLabel !== 'All / unspecified trunks' ? card.trunksLabel : card.name
          )}</div>
          <div class="arrow"></div>
          ${
            hasTimeSplit
              ? `<div class="node tc">${escapeHtml(tcLabel)}<small>${escapeHtml(tcSub)}</small></div>`
              : ''
          }
          <div class="lanes">
            ${
              branches.length
                ? branches.map(({ branch, lane }) => renderBranchHtml(branch, lane, greetings)).join('')
                : '<p style="padding:12px;color:#6b7280">No destination configured for this route.</p>'
            }
          </div>
        </div>
        ${
          card.otherQueues.length
            ? `<div class="otherq"><div class="lbl">Other queues (not on the office menu)</div><div class="chips">${card.otherQueues
                .map(
                  (q) =>
                    `<div class="chip"><b>${escapeHtml(q.number || q.id)}</b> ${escapeHtml(
                      q.name
                    )}<span class="m">${escapeHtml(
                      [
                        q.agents
                          .map((a) => (a.name ? `${a.number} ${a.name}` : a.number))
                          .join(', ') || 'No agents',
                        q.ringStrategy,
                        q.maxWaitTime != null ? `up to ${q.maxWaitTime} sec` : '',
                      ]
                        .filter(Boolean)
                        .join(' · ')
                    )}</span></div>`
                )
                .join('')}</div></div>`
            : ''
        }
      </section>`;
    })
    .join('\n');

  const updated = new Date(report.generatedAt).toLocaleString();

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1.0"/>
<title>${escapeHtml(report.customerName)} — Inbound Call Routing</title>
<style>
${REPORT_CSS_BASE}
  .legend{display:flex;flex-wrap:wrap;gap:14px;margin:0 0 22px;font-size:12.5px;}
  .legend span{display:inline-flex;align-items:center;gap:6px;}
  .dot{width:12px;height:12px;border-radius:3px;display:inline-block;}
  .company{background:var(--card);border:1px solid var(--line);border-radius:14px;
           box-shadow:0 1px 3px rgba(16,24,40,.06);margin-bottom:24px;overflow:hidden;break-inside:avoid;}
  .chead{padding:16px 20px;display:flex;flex-wrap:wrap;align-items:center;gap:10px 16px;border-bottom:1px solid var(--line);}
  .chead .badge{font-weight:700;font-size:12px;color:#fff;padding:4px 10px;border-radius:6px;background:var(--accent);}
  .chead h2{margin:0;font-size:18px;flex:1 1 auto;}
  .cfacts{display:flex;flex-wrap:wrap;gap:6px 22px;padding:10px 20px;background:#fafbfc;border-bottom:1px solid var(--line);font-size:12.5px;color:var(--muted);}
  .cfacts b{color:var(--ink);font-weight:600;}
  .flow{padding:20px;}
  .node{border-radius:9px;padding:9px 14px;text-align:center;font-size:13px;font-weight:600;}
  .incoming{background:#111827;color:#fff;max-width:420px;margin:0 auto;}
  .arrow{width:2px;background:var(--line);height:18px;margin:0 auto;}
  .arrow.small{height:12px;}
  .tc{background:#eef2ff;border:1px solid #c7d2fe;color:#3730a3;max-width:420px;margin:0 auto;}
  .tc small{display:block;font-weight:500;color:#4f46e5;margin-top:2px;font-size:11.5px;}
  .lanes{display:flex;gap:18px;margin-top:14px;flex-wrap:wrap;}
  .lane{flex:1 1 280px;min-width:0;}
  .lanehd{font-size:11px;font-weight:700;letter-spacing:.6px;text-transform:uppercase;text-align:center;margin-bottom:8px;}
  .lane.office .lanehd{color:var(--office);}
  .lane.after .lanehd,.lane.holiday .lanehd{color:var(--after);}
  .ivr{border-radius:9px;padding:9px 12px;text-align:center;font-size:12.5px;font-weight:600;}
  .ivr.office{background:#ecfdf5;border:1px solid #99f6e4;color:var(--office);}
  .ivr.after{background:#fffbeb;border:1px solid #fde68a;color:var(--after);}
  .ivr .vn{font-size:11px;font-weight:500;opacity:.8;display:block;margin-top:2px;}
  .ivr.unknown{background:#f8fafc;border:1px dashed #cbd5e1;color:var(--muted);}
  .lane.unknown .lanehd{color:var(--muted);}
  .lanehd .live{background:#059669;color:#fff;border-radius:4px;padding:1px 6px;font-size:10px;letter-spacing:0;text-transform:none;margin-left:4px;}
  .note{margin-top:5px;font-size:10.5px;color:var(--muted);text-align:center;}
  .lanehd .hist{background:#fef3c7;color:#92400e;border-radius:4px;padding:1px 6px;font-size:10px;letter-spacing:0;text-transform:none;margin-left:4px;}
  .greet{margin-top:10px;border:1px solid var(--line);border-radius:8px;background:#fff;padding:8px 10px;font-size:11.5px;text-align:left;}
  .greet .gh{font-weight:700;color:#334155;}
  .greet .gt{margin-top:4px;font-style:italic;color:#334155;}
  .greet .gm,.greet .gs{margin-top:3px;color:var(--muted);font-size:10.5px;}
  .greet .gc{margin-top:6px;border-radius:6px;padding:5px 8px;}
  .greet .gc.ok{background:#ecfdf5;color:#065f46;} .greet .gc.warn{background:#fffbeb;color:#92400e;}
  .greet .gc div{margin-top:2px;}
  .said{font-size:10.5px;margin-top:2px;} .said.ok{color:#047857;} .said.warn{color:#b45309;}
  .unlinked{background:var(--card);border:1px solid var(--line);border-radius:14px;padding:18px 20px;margin-bottom:24px;break-inside:avoid;}
  .unlinked h2{margin:0 0 4px;font-size:16px;}
  .unlinked p{margin:0 0 12px;font-size:12px;color:var(--muted);}
  .menu{margin-top:10px;display:flex;flex-direction:column;gap:7px;}
  .opt{display:flex;align-items:stretch;gap:8px;}
  .key{flex:0 0 auto;width:30px;display:flex;align-items:center;justify-content:center;
       background:var(--office);color:#fff;font-weight:700;border-radius:7px;font-size:13px;}
  .dest{flex:1 1 auto;border:1px solid var(--line);border-radius:7px;padding:6px 10px;font-size:12px;background:#fff;text-align:left;}
  .dest .t{font-weight:600;}
  .tag{font-size:10px;font-weight:700;padding:1px 6px;border-radius:4px;color:#fff;margin-right:6px;vertical-align:1px;}
  .tag.q{background:var(--queue);} .tag.e{background:var(--ext);}
  .members{color:var(--muted);font-size:11px;margin-top:2px;}
  .qmeta{margin-top:3px;font-size:10.5px;color:#334155;background:#eff3f8;border-radius:5px;padding:2px 7px;display:inline-block;}
  .afterbox{margin-top:10px;border:1px dashed #fde68a;background:#fffdf5;border-radius:8px;padding:10px 12px;font-size:12px;color:var(--after);text-align:center;}
  .otherq{padding:0 20px 18px;}
  .otherq .lbl{font-size:11px;font-weight:700;letter-spacing:.5px;text-transform:uppercase;color:var(--muted);margin:4px 0 8px;}
  .otherq .chips{display:flex;flex-wrap:wrap;gap:8px;}
  .chip{border:1px solid var(--line);border-radius:7px;padding:5px 9px;font-size:11.5px;background:#fff;}
  .chip b{color:var(--queue);}
  .chip .m{display:block;color:#334155;font-size:10.5px;margin-top:2px;}
  @media(max-width:680px){ .lanes{flex-direction:column} }
</style>
</head>
<body>
<div class="wrap">
  <header class="top">
    <h1>Inbound Call Routing — ${escapeHtml(report.customerName)}</h1>
    <div class="sub">How calls flow from public trunks into IVRs, queues, and extensions.</div>
    <div class="meta">Yeastar P-Series &nbsp;&bull;&nbsp; ${report.cards.length} inbound route(s) &nbsp;&bull;&nbsp; Generated ${escapeHtml(updated)}</div>
  </header>
  <div class="legend">
    <span><i class="dot" style="background:#111827"></i> Incoming call</span>
    <span><i class="dot" style="background:#6366f1"></i> Time condition</span>
    <span><i class="dot" style="background:#0f766e"></i> Office hours</span>
    <span><i class="dot" style="background:#9a6700"></i> After hours / holidays</span>
    <span><i class="dot" style="background:#1d4ed8"></i> Queue</span>
    <span><i class="dot" style="background:#7c3aed"></i> Extension / other</span>
  </div>
  ${cards || '<p>No inbound routes found on this PBX.</p>'}
  ${
    report.unlinkedIvrs.length
      ? `<section class="unlinked"><h2>Other IVR menus on this PBX</h2><p>The PBX did not say where some time periods go, so here are the IVRs not shown above.</p><div class="lanes">${report.unlinkedIvrs
          .map((ivr) =>
            renderBranchHtml(
              {
                title: ivr.name,
                dest: {
                  type: 'ivr',
                  value: ivr.id,
                  label: ivr.number ? `${ivr.name} (${ivr.number})` : ivr.name,
                  kind: 'ivr',
                },
                ivr,
              },
              'after',
              greetings
            )
          )
          .join('')}</div></section>`
      : ''
  }
  <div class="footer">
    <p>Generated from live PBX configuration via Yeastarify (inbound_route/get + ivr/get + queue/get). Where the PBX does not say where a custom time period goes (firmware before xx.24), the destination comes from the most recent calls in that time, and is marked "from call history".</p>
  </div>
</div>
</body>
</html>`;
}

function formatDialPatternLabel(pattern: OutboundDialPattern): string {
  const bits = [pattern.pattern || '—'];
  if (pattern.strip != null && !Number.isNaN(pattern.strip) && pattern.strip > 0) {
    bits.push(`strip ${pattern.strip}`);
  }
  if (pattern.prepend) {
    bits.push(`prepend ${pattern.prepend}`);
  }
  return bits.join(' · ');
}

function formatOutboundTrunks(
  route: OutboundRoute,
  trunks: Map<string, TrunkInfo>
): string {
  const list = route.trunk_list?.length
    ? route.trunk_list
    : route.trunk?.length
      ? route.trunk
      : [];
  if (!list.length) return 'No trunks configured';
  return list
    .map((t) => {
      const id = String(t.id);
      const known = trunks.get(id);
      if (known) {
        const cid = known.defOutboundCid ? ` (${known.defOutboundCid})` : '';
        return `${known.name}${cid}`;
      }
      return t.name || `Trunk ${id}`;
    })
    .join(' · ');
}

function formatPinProtect(route: OutboundRoute): string {
  const mode = (route.pin_protect || 'disable').toLowerCase();
  if (!mode || mode === 'disable' || mode === 'disabled') return 'None';
  if (mode === 'single_pin') {
    return route.pin ? `Single PIN (${route.pin})` : 'Single PIN';
  }
  if (mode === 'pin_list') {
    return route.pin_list != null ? `PIN list #${route.pin_list}` : 'PIN list';
  }
  return route.pin_protect || 'Unknown';
}

function resolveGroupMembers(
  groupId: string,
  groupName: string,
  groupsById: Map<string, ExtensionGroupInfo>,
  groupsByName: Map<string, ExtensionGroupInfo>,
  extensionsById: Map<string, Extension>,
  extensionsByNumber: Map<string, Extension>
): { members: OutboundExtensionMember[]; unresolved: boolean } {
  const group =
    groupsById.get(groupId) ||
    groupsByName.get(normalizeKey(groupName)) ||
    undefined;

  if (!group) {
    return { members: [], unresolved: true };
  }

  const seen = new Set<string>();
  const members: OutboundExtensionMember[] = [];

  const pushExt = (ext: Extension | undefined, fallbackNumber?: string) => {
    const number = ext?.number || fallbackNumber || '';
    if (!number || seen.has(number)) return;
    seen.add(number);
    members.push({
      number,
      name: ext ? extensionDisplayName(ext) : '—',
    });
  };

  for (const num of group.memberExtensionNumbers || []) {
    pushExt(extensionsByNumber.get(num), num);
  }
  for (const id of group.memberExtensionIds || []) {
    pushExt(extensionsById.get(id));
  }

  members.sort((a, b) =>
    a.number.localeCompare(b.number, undefined, { numeric: true })
  );

  // Group exists but API did not expose members
  const unresolved = members.length === 0 && !groupHasPbxMembers(group);
  return { members, unresolved };
}

function resolveOutboundPermissions(
  extList: OutboundExtListItem[] | undefined,
  groupsById: Map<string, ExtensionGroupInfo>,
  groupsByName: Map<string, ExtensionGroupInfo>,
  extensionsById: Map<string, Extension>,
  extensionsByNumber: Map<string, Extension>
): OutboundPermissionEntry[] {
  if (!extList?.length) return [];

  const entries: OutboundPermissionEntry[] = [];

  for (const item of extList) {
    const type = (item.type || 'extension').toLowerCase();
    const id = item.id != null ? String(item.id) : undefined;
    const rawName =
      item.ext_name || item.name || item.ext_number || item.number || id || 'Unknown';

    if (type === 'extension' || type === 'ext') {
      const number =
        item.ext_number ||
        item.number ||
        (id ? extensionsById.get(id)?.number : '') ||
        '';
      const ext =
        (number && extensionsByNumber.get(number)) ||
        (id ? extensionsById.get(id) : undefined);
      const name = ext ? extensionDisplayName(ext) : rawName;
      entries.push({
        kind: 'extension',
        id,
        label: number ? `${number} ${name}`.trim() : name,
        memberCount: number ? 1 : 0,
        members: number
          ? [{ number, name: name === number ? '—' : name }]
          : [],
      });
      continue;
    }

    if (type === 'ext_group' || type === 'extension_group' || type === 'group') {
      const groupName = rawName;
      const { members, unresolved } = resolveGroupMembers(
        id || '',
        groupName,
        groupsById,
        groupsByName,
        extensionsById,
        extensionsByNumber
      );
      entries.push({
        kind: 'ext_group',
        id,
        label: groupName,
        memberCount: members.length,
        members,
        membersUnresolved: unresolved,
      });
      continue;
    }

    if (type === 'organization' || type === 'org') {
      // Organizations are hierarchical; PBX may not expand them via extension_group API.
      // Treat as a permission unit; if id matches a group, still try to expand.
      const { members, unresolved } = resolveGroupMembers(
        id || '',
        rawName,
        groupsById,
        groupsByName,
        extensionsById,
        extensionsByNumber
      );
      entries.push({
        kind: 'organization',
        id,
        label: rawName,
        memberCount: members.length,
        members,
        membersUnresolved: unresolved && members.length === 0,
      });
      continue;
    }

    entries.push({
      kind: 'other',
      id,
      label: `${type}: ${rawName}`,
      memberCount: 0,
      members: [],
      membersUnresolved: true,
    });
  }

  // Groups first, then extensions, then orgs
  const order = { ext_group: 0, organization: 1, extension: 2, other: 3 } as const;
  entries.sort((a, b) => {
    const oa = order[a.kind] ?? 9;
    const ob = order[b.kind] ?? 9;
    if (oa !== ob) return oa - ob;
    return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
  });

  return entries;
}

function flattenDialers(permissions: OutboundPermissionEntry[]): OutboundExtensionMember[] {
  const byNumber = new Map<string, OutboundExtensionMember>();
  for (const entry of permissions) {
    for (const member of entry.members) {
      if (!member.number) continue;
      if (!byNumber.has(member.number)) {
        byNumber.set(member.number, member);
      }
    }
  }
  return Array.from(byNumber.values()).sort((a, b) =>
    a.number.localeCompare(b.number, undefined, { numeric: true })
  );
}

function mergeOutboundRouteDetails(
  listRoute: OutboundRoute,
  getRoute: OutboundRoute | null
): OutboundRoute {
  if (!getRoute) return listRoute;
  return {
    ...listRoute,
    ...getRoute,
    // Prefer get's detailed lists; fall back to list
    dial_pattern_list: getRoute.dial_pattern_list?.length
      ? getRoute.dial_pattern_list
      : listRoute.dial_pattern_list,
    trunk_list: getRoute.trunk_list?.length
      ? getRoute.trunk_list
      : listRoute.trunk_list,
    ext_list: getRoute.ext_list?.length ? getRoute.ext_list : listRoute.ext_list,
    name: getRoute.name || listRoute.name,
    pos: getRoute.pos ?? listRoute.pos,
  };
}

export async function buildOutboundRoutingReport(
  customerName: string
): Promise<OutboundRoutingReport> {
  const [routeList, extensions, groupInfos, trunks] = await Promise.all([
    fetchOutboundRouteList(false),
    fetchExtensions(false),
    fetchExtensionGroups(false),
    fetchTrunks(false),
  ]);

  const detailedRoutes = (
    await Promise.all(
      routeList.map(async (route) => {
        try {
          const full = await getOutboundRoute(route.id);
          return mergeOutboundRouteDetails(route, full);
        } catch {
          return route;
        }
      })
    )
  ).filter(Boolean) as OutboundRoute[];

  // Stable order by pos then name
  detailedRoutes.sort((a, b) => {
    const pa = a.pos ?? 9999;
    const pb = b.pos ?? 9999;
    if (pa !== pb) return pa - pb;
    return (a.name || '').localeCompare(b.name || '', undefined, {
      sensitivity: 'base',
    });
  });

  const extensionsById = new Map(extensions.map((e) => [e.id, e]));
  const extensionsByNumber = new Map(extensions.map((e) => [e.number, e]));
  const groupsById = new Map(groupInfos.map((g) => [g.id, g]));
  const groupsByName = new Map(groupInfos.map((g) => [normalizeKey(g.name), g]));
  const trunkMap = new Map(trunks.map((t) => [t.id, t]));

  const cards: OutboundRouteReportCard[] = detailedRoutes.map((route) => {
    const rawPatterns: OutboundDialPattern[] = route.dial_pattern_list?.length
      ? route.dial_pattern_list
      : (route.dial_pattern || []).map((p) => ({ pattern: p }));
    const patterns = rawPatterns.map((p) => ({
      pattern: p.pattern,
      strip: p.strip,
      prepend: p.prepend,
      label: formatDialPatternLabel(p),
    }));

    const permissions = resolveOutboundPermissions(
      route.ext_list,
      groupsById,
      groupsByName,
      extensionsById,
      extensionsByNumber
    );
    const allDialers = flattenDialers(permissions);

    return {
      id: route.id,
      name: route.name || `Route ${route.id}`,
      pos: route.pos,
      trunksLabel: formatOutboundTrunks(route, trunkMap),
      outboundCidLabel: route.outbound_cid?.trim() || 'Default / trunk CID',
      pinLabel: formatPinProtect(route),
      dialPatterns: patterns,
      permissions,
      allDialers,
      dialerCount: allDialers.length,
    };
  });

  const uniqueDialers = new Set<string>();
  for (const card of cards) {
    for (const d of card.allDialers) uniqueDialers.add(d.number);
  }

  return {
    customerName,
    generatedAt: new Date().toISOString(),
    cards,
    totalRoutes: cards.length,
    totalUniqueDialers: uniqueDialers.size,
  };
}

function permissionKindLabel(kind: OutboundPermissionEntry['kind']): string {
  if (kind === 'ext_group') return 'EXT GROUP';
  if (kind === 'organization') return 'ORG';
  if (kind === 'extension') return 'EXT';
  return 'OTHER';
}

export function renderOutboundRoutingHtml(report: OutboundRoutingReport): string {
  const cards = report.cards
    .map((card) => {
      const patternsHtml = card.dialPatterns.length
        ? card.dialPatterns
            .map(
              (p) =>
                `<span class="pat">${escapeHtml(p.label)}</span>`
            )
            .join('')
        : '<span class="pat muted">No dial patterns</span>';

      const permissionsHtml = card.permissions.length
        ? card.permissions
            .map((entry) => {
              const tagClass =
                entry.kind === 'ext_group'
                  ? 'g'
                  : entry.kind === 'organization'
                    ? 'o'
                    : entry.kind === 'extension'
                      ? 'e'
                      : 'x';
              // Members render as pills in the flattened dialers section — skip
              // the long comma-separated text under groups to avoid duplication.
              const membersHtml =
                entry.kind !== 'extension' &&
                entry.members.length === 0 &&
                entry.membersUnresolved
                  ? `<div class="members warn">Members not available from PBX API</div>`
                  : '';
              const countBadge =
                entry.kind !== 'extension'
                  ? `<span class="cnt">${entry.memberCount} member${
                      entry.memberCount === 1 ? '' : 's'
                    }</span>`
                  : '';
              return `
              <div class="perm">
                <div class="permhd">
                  <span class="tag ${tagClass}">${escapeHtml(
                    permissionKindLabel(entry.kind)
                  )}</span>
                  <span class="plabel">${escapeHtml(entry.label)}</span>
                  ${countBadge}
                </div>
                ${membersHtml}
              </div>`;
            })
            .join('')
        : '<p class="empty">No extensions or groups selected — nobody can dial out on this route.</p>';

      const dialersHtml =
        card.allDialers.length > 0
          ? `<div class="dialers"><div class="lbl">All extensions that can dial out (${
              card.dialerCount
            })</div><div class="chips">${card.allDialers
              .map(
                (d) =>
                  `<span class="chip"><b>${escapeHtml(d.number)}</b> ${escapeHtml(
                    d.name === '—' ? '' : d.name
                  )}</span>`
              )
              .join('')}</div></div>`
          : '';

      return `
      <section class="company">
        <div class="chead">
          <span class="badge">OUTBOUND</span>
          <h2>${escapeHtml(card.name)}</h2>
          ${
            card.pos != null
              ? `<span class="pos">Priority ${escapeHtml(String(card.pos))}</span>`
              : ''
          }
        </div>
        <div class="cfacts">
          <span>Trunks: <b>${escapeHtml(card.trunksLabel)}</b></span>
          <span>Outbound CID: <b>${escapeHtml(card.outboundCidLabel)}</b></span>
          <span>PIN: <b>${escapeHtml(card.pinLabel)}</b></span>
          <span>Dialers: <b>${card.dialerCount}</b></span>
        </div>
        <div class="body">
          <div class="section">
            <div class="shd">Dial patterns</div>
            <div class="pats">${patternsHtml}</div>
          </div>
          <div class="section">
            <div class="shd">Who can dial out</div>
            <div class="perms">${permissionsHtml}</div>
          </div>
          ${dialersHtml}
        </div>
      </section>`;
    })
    .join('\n');

  const updated = new Date(report.generatedAt).toLocaleString();

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1.0"/>
<title>${escapeHtml(report.customerName)} — Outbound Call Routing</title>
<style>
${REPORT_CSS_BASE}
  .legend{display:flex;flex-wrap:wrap;gap:14px;margin:0 0 22px;font-size:12.5px;}
  .legend span{display:inline-flex;align-items:center;gap:6px;}
  .dot{width:12px;height:12px;border-radius:3px;display:inline-block;}
  .company{background:var(--card);border:1px solid var(--line);border-radius:14px;
           box-shadow:0 1px 3px rgba(16,24,40,.06);margin-bottom:24px;overflow:hidden;break-inside:avoid;}
  .chead{padding:16px 20px;display:flex;flex-wrap:wrap;align-items:center;gap:10px 16px;border-bottom:1px solid var(--line);}
  .chead .badge{font-weight:700;font-size:12px;color:#fff;padding:4px 10px;border-radius:6px;background:#0d9488;}
  .chead h2{margin:0;font-size:18px;flex:1 1 auto;}
  .chead .pos{font-size:12px;color:var(--muted);font-weight:600;}
  .cfacts{display:flex;flex-wrap:wrap;gap:6px 22px;padding:10px 20px;background:#fafbfc;border-bottom:1px solid var(--line);font-size:12.5px;color:var(--muted);}
  .cfacts b{color:var(--ink);font-weight:600;}
  .body{padding:18px 20px 20px;}
  .section{margin-bottom:16px;}
  .shd{font-size:11px;font-weight:700;letter-spacing:.5px;text-transform:uppercase;color:var(--muted);margin-bottom:8px;}
  .pats{display:flex;flex-wrap:wrap;gap:8px;}
  .pat{display:inline-block;background:#ecfdf5;border:1px solid #99f6e4;color:#0f766e;
       font-size:12.5px;font-weight:600;padding:5px 10px;border-radius:7px;font-variant-numeric:tabular-nums;}
  .pat.muted{background:#f8fafc;border-color:var(--line);color:var(--muted);font-weight:500;}
  .perms{display:flex;flex-direction:column;gap:10px;}
  .perm{border:1px solid var(--line);border-radius:10px;padding:10px 12px;background:#fff;}
  .permhd{display:flex;flex-wrap:wrap;align-items:center;gap:8px;}
  .plabel{font-weight:600;font-size:13.5px;}
  .cnt{font-size:11px;color:var(--muted);background:#f1f5f9;padding:2px 8px;border-radius:999px;}
  .tag{font-size:10px;font-weight:700;padding:2px 7px;border-radius:4px;color:#fff;letter-spacing:.3px;}
  .tag.g{background:#0d9488;} .tag.e{background:#7c3aed;} .tag.o{background:#b45309;} .tag.x{background:#64748b;}
  .members{color:var(--muted);font-size:12px;margin-top:6px;line-height:1.45;}
  .members.warn{color:var(--warn);background:var(--warnbg);padding:4px 8px;border-radius:6px;display:inline-block;}
  .members.muted{font-style:italic;}
  .empty{color:var(--muted);font-size:13px;margin:0;}
  .dialers{margin-top:6px;padding-top:14px;border-top:1px solid var(--line);}
  .dialers .lbl{font-size:11px;font-weight:700;letter-spacing:.5px;text-transform:uppercase;color:var(--muted);margin-bottom:8px;}
  .chips{display:flex;flex-wrap:wrap;gap:6px;}
  .chip{border:1px solid var(--line);border-radius:7px;padding:4px 8px;font-size:11.5px;background:#fafbfc;}
  .chip b{color:#7c3aed;font-variant-numeric:tabular-nums;margin-right:3px;}
</style>
</head>
<body>
<div class="wrap">
  <header class="top">
    <h1>Outbound Call Routing — ${escapeHtml(report.customerName)}</h1>
    <div class="sub">Which extensions and extension groups can dial out on each outbound route.</div>
    <div class="meta">Yeastar P-Series &nbsp;&bull;&nbsp; ${report.totalRoutes} outbound route(s) &nbsp;&bull;&nbsp; ${report.totalUniqueDialers} unique dialer(s) &nbsp;&bull;&nbsp; Generated ${escapeHtml(updated)}</div>
  </header>
  <div class="legend">
    <span><i class="dot" style="background:#0d9488"></i> Extension group</span>
    <span><i class="dot" style="background:#7c3aed"></i> Individual extension</span>
    <span><i class="dot" style="background:#b45309"></i> Organization</span>
    <span><i class="dot" style="background:#0f766e"></i> Dial pattern</span>
  </div>
  ${cards || '<p>No outbound routes found on this PBX.</p>'}
  <div class="footer">
    <p>Generated from live PBX configuration via Yeastarify (outbound_route/get + extension_group/query). Extension group members are expanded when the PBX exposes membership.</p>
  </div>
</div>
</body>
</html>`;
}

/** One IVR as sent to ivrPrompts:transcribeGreetings. */
export type GreetingRequestIvr = {
  id: string;
  name: string;
  number?: string;
  prompt?: string;
  menu: Array<{ key: string; label: string }>;
  timeoutLabel?: string;
};

/** What an IVR's greeting says, from ivrPrompts:transcribeGreetings. */
export type IvrGreeting = {
  ivrId: string;
  status: 'ok' | 'none' | 'error';
  transcript?: string;
  source?: 'prompt' | 'recording';
  recordedAt?: string;
  check?: {
    ok: boolean;
    keys: Array<{ key: string; said: string; goesTo: string; ok: boolean; note?: string }>;
    notes: string[];
  };
  message?: string;
};

/** The IVRs the report shows a panel for, with their key setup, for transcribing greetings. */
export function greetingRequest(report: InboundRoutingReport): GreetingRequestIvr[] {
  const ivrs = new Map<string, ResolvedIvr>();
  const played = new Map<string, GreetingRequestIvr>();
  for (const card of report.cards) {
    for (const branch of [card.alwaysDest, card.officeHours, card.afterHours, card.holidays, ...(card.periodLanes || [])]) {
      if (branch?.ivr) ivrs.set(branch.ivr.id, branch.ivr);
      const key = branch && greetingKey(branch);
      if (key && !branch.ivr) {
        played.set(key, { id: key, name: `Greeting ${branch.dest.value}`, prompt: branch.dest.value, menu: [] });
      }
    }
  }
  for (const ivr of report.unlinkedIvrs) ivrs.set(ivr.id, ivr);
  return [...Array.from(played.values()), ...Array.from(ivrs.values()).map((ivr) => ({
    id: ivr.id,
    name: ivr.name,
    ...(ivr.number ? { number: ivr.number } : {}),
    ...(ivr.prompt ? { prompt: ivr.prompt } : {}),
    menu: ivr.menuResolved.map((opt) => {
      const agents = opt.queueDetail?.agents
        .map((a) => (a.name ? `${a.number} ${a.name}` : a.number))
        .join(', ');
      return { key: opt.key, label: agents ? `${opt.resolvedLabel}, agents: ${agents}` : opt.resolvedLabel };
    }),
    ...(ivr.timeoutLabel ? { timeoutLabel: ivr.timeoutLabel } : {}),
  }))];
}

/** Which greeting a branch plays: its IVR's id, or "greeting:<file>" for a played greeting. */
export function greetingKey(branch: DestinationBranch): string | undefined {
  if (branch.ivr) return branch.ivr.id;
  if (branch.dest.type.toLowerCase() === 'play_greeting' && branch.dest.value) return `greeting:${branch.dest.value}`;
  return undefined;
}
